import React, { useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from '@material-ui/core';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth/withWidth';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { connectInfiniteHits } from 'react-instantsearch-dom';

import { DiscoverContext } from '../../../../context/DiscoverContext';
import {
  GET_DISCOVER_PATHWAYS_DETAILED_INFO,
  GET_VIDEOS_EXIST_IN_USER_WATCH_LIST,
} from '../../../../graphql/pathways';
import { GET_VIDEO_PROGRESS_LIST } from '../../../../graphql/videos';
import {
  Query,
  QueryGet_Discover_Pathways_Detailed_InfoArgs,
  QueryVideosExistInUserWatchListArgs,
  VideoContinueWatchingLinksArgs,
} from '../../../../types';
import { collectValueByKey } from '../../../../utils/helpers';
import { useSERPNavigation } from '../../../../utils/hooks';
import { HITS_TYPES } from '../../const';
import { GlossaryGridHit } from '../GlossaryGridHit';
import { GlossaryInfiniteHist } from '../GlossaryInfiniteHits';
import GridHit from '../GridHit';
import { Hit } from '../Hit';
import useStyles from './styles';

export const InfiniteHits = ({
  hits,
  hasMore,
  refineNext,
  page,
  limit,
  total,
  view,
  width,
  userInfo,
  tenantTitle,
  tenantName = null,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(view);
  const isDesktop = isWidthUp('md', width);

  const isGridView = view === 'grid' && isDesktop;
  const showedItems = (page + 1) * limit;

  const serpType = useSERPNavigation();
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onVideoPage = serpType === HITS_TYPES.VIDEO;

  const { searchState } = useContext(DiscoverContext);

  const [pathwayIds, setPathwayIds] = useState([]);
  const [videoIds, setVideoIds] = useState([]);

  const { data: pathwaysProgressData } = useQuery<
    Pick<Query, 'get_discover_pathways_detailed_info'>,
    QueryGet_Discover_Pathways_Detailed_InfoArgs
  >(GET_DISCOVER_PATHWAYS_DETAILED_INFO, {
    variables: {
      pathway_ids: pathwayIds,
    },
    skip: pathwayIds.length === 0 || isGridView || !userInfo,
    fetchPolicy: 'network-only',
  });

  const pathwaysProgress = pathwaysProgressData?.get_discover_pathways_detailed_info || [];

  const { data: videoProgressData } = useQuery<
    Pick<Query, 'listVideoProgress'>,
    VideoContinueWatchingLinksArgs
  >(GET_VIDEO_PROGRESS_LIST, {
    variables: {
      filter: {
        user_id: userInfo?.id,
        video_ids: videoIds,
      },
    },
    skip: !userInfo?.id || videoIds.length === 0 || isGridView,
    fetchPolicy: 'network-only',
  });

  const videosProgress = videoProgressData?.listVideoProgress || [];

  const videosIds = onVideoPage
    ? hits?.map((entity) => entity?.id)?.filter((item): item is string => Boolean(item))
    : null;

  const { data: videosInWatchlist } = useQuery<
    Pick<Query, 'VideosExistInUserWatchList'>,
    QueryVideosExistInUserWatchListArgs
  >(GET_VIDEOS_EXIST_IN_USER_WATCH_LIST, {
    variables: {
      video_ids: videosIds,
    },
    skip: !videosIds || !videosIds?.length || !userInfo,
  });

  const watchlistById =
    videosInWatchlist?.VideosExistInUserWatchList?.reduce(
      collectValueByKey('video_id', 'exists'),
      {},
    ) || {};

  useEffect(() => {
    if (hits && hits.length > 0) {
      const newVideosIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.VIDEO)
        .map((item) => item.id.toString());
      setVideoIds(newVideosIds);

      const newPathwaysIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.PATHWAY)
        .map((item) => item.id);
      setPathwayIds(newPathwaysIds);
    }
  }, [hits]);

  return (
    <div className={classes.wrapper}>
      <ul
        className={cx(
          isGridView ? 'ais-InfiniteHits-grid' : 'ais-InfiniteHits-list',
          onGlossaryPage && 'ais-GlossaryInfiniteHits-list',
        )}
      >
        {onGlossaryPage && !searchState?.query ? (
          <GlossaryInfiniteHist hits={hits} tenantTitle={tenantTitle} isGridView={isGridView} />
        ) : (
          hits.map((hit) => {
            const pathwayProgress = pathwaysProgress.find(
              (item) => item?.id === hit?.id && hit?.searchable_type === HITS_TYPES.PATHWAY,
            );
            const videoProgress = videosProgress.find(
              (item) =>
                item?.video?.id === hit?.id.toString() && hit?.searchable_type === HITS_TYPES.VIDEO,
            );

            return (
              <li data-testid="resultItem" key={hit.objectID}>
                {isGridView ? (
                  hit?.searchable_type === HITS_TYPES.GLOSSARY ? (
                    <GlossaryGridHit
                      slug={hit?.slug}
                      title={hit?.title}
                      description={hit?.definition}
                      categories={hit?.categories_array}
                      videoUrl={hit?.videoUrl}
                    />
                  ) : (
                    <GridHit hit={hit} />
                  )
                ) : (
                  <Hit
                    hit={hit}
                    pathwayProgress={pathwayProgress}
                    videoProgress={videoProgress}
                    tenantTitle={tenantTitle}
                    tenantName={tenantName}
                    isInWatchlist={watchlistById[hit.id]}
                  />
                )}
              </li>
            );
          })
        )}
      </ul>
      {hasMore && (
        <Button className={classes.viewBtn} onClick={refineNext}>
          {t('button.viewMore')} - {showedItems} {t('button.of')} {total}
        </Button>
      )}
    </div>
  );
};

export default withWidth()(connectInfiniteHits(InfiniteHits));
